/**
 * The container displaying the components to set the parameters
 * of a Duplo module.
 *
 * Copyright © Ircam amplify 2024-2025
 */
import { TextUtil } from '@amplify3/duplo-back-end'
import { AutoComplete } from '../Autocomplete'
import RectRoundButton from '../Button/RectRoundButton'
import IosSwitch from '../IosSwitch'
import { ModuleParam, ModuleParamType } from '../ModuleConfig/ModuleParam'
import './ModuleParamContainer.css'

import DurationSpinner from './DurationSpinner'
import TipComp from './TipComp'
const IOS_SWITCH_HEIGHT = 17
const IOS_SWITCH_WIDTH = 30

interface ModuleParamContainerProps {
  moduleParams?: ModuleParam<any>[]
  paramsTip?: string
  configParamList?: Record<string, any>
  setConfigParam: (paramId: string, value: any) => void
  title: string
  sourceDuration: number
}

function ModuleParamContainer({
  moduleParams,
  configParamList,
  setConfigParam,
  title = '',
  paramsTip = '',
  sourceDuration = 0,
}: ModuleParamContainerProps) {
  /**
   * Handle the change of a parameter's component.
   */
  const handleChange = (event: any) => {
    // Real DOM events (e.g. the button group below) carry `name`/`value`
    // on `currentTarget`, not `target`, since a click can land on a nested
    // child element. AutoComplete/DurationSpinner instead call this with a
    // plain `{ target: { name, value } }` object that has no `currentTarget`.
    const { name: paramName, value } = event.currentTarget ?? event.target
    setConfigParam(paramName, value)
  }

  /**
   * Handle the change of the step slider.
   */
  const handleChangeStepSlider = (event: any, values: any) => {
    const { name: paramName, value } = event.target
    setConfigParam(paramName, values[value])
  }

  /**
   * Handle the change of a parameter's component.
   */
  const handleCheckChange = (event: any) => {
    const { name: paramName, checked } = event.target
    setConfigParam(paramName, checked)
  }

  return (
    <div className="module-param-container" id="module-param-container">
      <h3>{title}</h3>
      <p>{paramsTip}</p>
      {moduleParams?.map((param: ModuleParam<any>) => {
        const paramIdSuffix = TextUtil.replaceSpaces(param.title.toLowerCase())

        return (
          <div className="jobui-section2" key={param.name}>
            <div className="paramTitle">
              <h4 id={`param-title-${paramIdSuffix}`}>
                {param.title} {param.tip && <TipComp message={param.tip} />}
              </h4>
              {(param.type === ModuleParamType.Slider ||
                param.type === ModuleParamType.StepSlider) && (
                <h4 id={`param-value-${paramIdSuffix}`}>
                  {configParamList?.[param.name] ?? param.defaultValue}{' '}
                  {typeof (
                    configParamList?.[param.name] ?? param.defaultValue
                  ) === 'number' && param.unit}
                </h4>
              )}
            </div>
            {param.tip2 && <div className="tip2">{param.tip2}</div>}

            {param.type === ModuleParamType.Button && (
              <div className="btnGrp">
                {param.options?.map((option: any) => (
                  <RectRoundButton
                    key={option.value}
                    id={param.getComponentId(String(option.value))}
                    onClick={handleChange}
                    name={param.name}
                    value={option.value}
                    label={option.title}
                    className={
                      configParamList?.[param.name] === option.value ||
                      (!configParamList?.[param.name] &&
                        option.value === 'None') ||
                      (!configParamList?.[param.name] && option.defaultValue)
                        ? 'optionBtn selected'
                        : 'optionBtn'
                    }
                  />
                ))}
              </div>
            )}

            {param.type === ModuleParamType.SwitchGroup && (
              <div className="switchGrp">
                {param.options?.map((option: any) => (
                  <IosSwitch
                    key={option.name}
                    id={param.getComponentId()}
                    height={IOS_SWITCH_HEIGHT}
                    width={IOS_SWITCH_WIDTH}
                    name={option.name}
                    defaultChecked={option.defaultValue}
                    onChange={handleCheckChange}
                    label={option.title}
                  />
                ))}
              </div>
            )}

            {param.type === ModuleParamType.AutoComplete && (
              <AutoComplete
                {...param}
                id={param.getComponentId()}
                handleChange={handleChange}
              />
            )}

            {param.type === ModuleParamType.Number && (
              <div>
                <input
                  type="number"
                  name={param.name}
                  id={param.getComponentId()}
                  onChange={handleChange}
                  min={param.min}
                  max={param.max}
                  step={param.step}
                  defaultValue={param.defaultValue}
                />{' '}
                {param.unit}
              </div>
            )}

            {param.type === ModuleParamType.Duration && (
              <DurationSpinner
                param={param}
                id={param.getComponentId()}
                setConfigParam={setConfigParam}
                sourceDuration={sourceDuration}
                handleChange={handleChange}
              />
            )}

            {param.type === ModuleParamType.Slider && (
              <div>
                <input
                  type="range"
                  name={param.name}
                  id={param.getComponentId()}
                  onChange={handleChange}
                  min={param.min}
                  max={param.max}
                  step={param.step}
                  defaultValue={param.defaultValue}
                />{' '}
              </div>
            )}

            {param.type === ModuleParamType.StepSlider && (
              <div>
                <input
                  type="range"
                  name={param.name}
                  id={param.getComponentId()}
                  onChange={(e) => handleChangeStepSlider(e, param.values)}
                  min={0}
                  max={(param.values?.length ?? 1) - 1}
                  step={1}
                  defaultValue={0}
                />{' '}
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

export default ModuleParamContainer
